import { Box, Divider, TextField } from "@mui/material"
import FormControl from "@mui/material/FormControl"
import FormControlLabel from "@mui/material/FormControlLabel"
import FormLabel from "@mui/material/FormLabel"
import Radio from "@mui/material/Radio"
import RadioGroup from "@mui/material/RadioGroup"
import { useEffect, useState, useCallback } from "react"
import { useTranslation } from "react-i18next"
import { PrintDirectionType, SelectPositionType, YesNoType } from "@/core/app/slices/user/user.type"
import { useAppDispatch } from "@/core/app/hooks"
import { debounce } from "lodash"

interface StandardModelPrintProps {
  currPrint: any
  updateModel: any
}

export const StandardModelPrint = ({
  currPrint,
  updateModel,
}: StandardModelPrintProps) => {
  const { t } = useTranslation("common")
  const [hollowModel, setHollowModel] = useState(0)
  const [drainHole, setDrainHole] = useState<number>(0)
  const [thickness, setThickness] = useState<number | string>(0)
  const [printDirection, setPrintDirection] = useState<number>(1)
  const [heightModel, setHeightModel] = useState<number | string>("")
  const dispatch = useAppDispatch()
  
  // Create update functions that immediately update the store
  const updateThickness = useCallback((val) => {
    const numVal = parseFloat(val)
    if (isNaN(numVal)) return
    const clamped = Math.min(Math.max(numVal, 2), 3)
    const finalValue = parseFloat(clamped.toFixed(1))
    dispatch(updateModel({ ShellThickness: finalValue }))
  }, [dispatch, updateModel])

  const updateHeight = useCallback((val) => {
    const numVal = parseFloat(val)
    if (isNaN(numVal)) return
    const clamped = Math.min(Math.max(numVal, 1), 20)
    const finalValue = parseFloat(clamped.toFixed(1))
    dispatch(updateModel({ HeightOfWall: finalValue }))
  }, [dispatch, updateModel])

  // Create debounced versions for the input handlers
  const debouncedUpdateThickness = useCallback(debounce(updateThickness, 500), [updateThickness])
  const debouncedUpdateHeight = useCallback(debounce(updateHeight, 500), [updateHeight])
  
  // Auto-adjust position based on GenerateShell value
  const updatePositionBasedOnShell = (shellValue: YesNoType, currentPosition: SelectPositionType) => {
    // Don't manipulate if value is LingualWall or no shell
    if (currentPosition === SelectPositionType.LingualWall ||  shellValue === YesNoType.No) {
      return
    }
    const positionMap = {
      [YesNoType.No]: SelectPositionType.Bottom,
      [YesNoType.Yes]: SelectPositionType.BuccalWall
    }
    
    const requiredPosition = positionMap[shellValue]
    if (requiredPosition !== undefined && currentPosition !== requiredPosition) {
      dispatch(updateModel({ SelectPositionLabel: requiredPosition }))
    }
  }
  const handleHollowModel = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = Number((event.target as HTMLInputElement).value)
    if (newValue !== hollowModel) {
      setHollowModel(newValue)
      dispatch(updateModel({ GenerateShell: newValue }))
      updatePositionBasedOnShell(newValue, currPrint?.SelectPositionLabel)
    }
  }

  const handleDrainHole = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = Number((event.target as HTMLInputElement).value)
    if (newValue !== drainHole) {
      setDrainHole(newValue)
      dispatch(updateModel({ DrainHole: newValue }))
    }
  }

  const handleInput = (e, min, max, setVal, callback) => {
    let value = e.target.value

    // Sanitize input ---
    value = value
      .replace(/[^0-9.]/g, "") // remove all non-numeric/dot characters
      .replace(/(\..*)\./g, "$1") // allow only one dot
      .replace(/^\./, `${min}.`) // if input starts with ".", prepend min

    // Restrict format (only 1 decimal place) ---
    value = value.replace(/(\.\d{1})\d+$/, "$1")

    // Allow empty value during typing
    if (value === "") {
      setVal("")
      return
    }

    // Update state and call debounced callback
    const numValue = value
    if (value !== "" && !isNaN(numValue)) {
        setVal(value)
        callback?.(value)
    }
  }
  

  const handlePrintDirection = (event: React.ChangeEvent<HTMLInputElement>) => {
    setPrintDirection(+(event.target as HTMLInputElement).value)
    dispatch(
      updateModel({
        PrintDirection: +(event.target as HTMLInputElement).value,
      }),
    )
  }

  useEffect(() => {
    if (currPrint?.GenerateShell !== undefined) {
      setHollowModel(currPrint?.GenerateShell)
    }
    if (currPrint?.DrainHole !== undefined) {
      setDrainHole(currPrint?.DrainHole)
    }
    if (currPrint?.PrintDirection !== printDirection) {
      setPrintDirection(currPrint?.PrintDirection)
    }
  }, [currPrint?.PrintDirection, currPrint?.DrainHole, currPrint?.GenerateShell])

  useEffect(() => {
    if (currPrint?.ShellThickness !== thickness) {
      setThickness(currPrint?.ShellThickness)
    }
  }, [currPrint?.ShellThickness])

  useEffect(() => {
    if (currPrint?.HeightOfWall !== heightModel) {
      setHeightModel(currPrint?.HeightOfWall)
    }
  }, [currPrint?.HeightOfWall])

  const styleObj = {
    height: "80px",
    borderRadius: "6px",
    flexDirection: "row",
  }

  return (
    <>
      <Box
        id="other1"
        sx={{
          display: "flex",
          gap: "24px",
          ...styleObj,
        }}
      >
        <FormControl sx={{ flex: 1 }}>
          <FormLabel id="hollow">{t("stlPrinting.hollowModel")}</FormLabel>
          <RadioGroup
            row
            sx={{
              marginLeft: "8px",
              gap: "40px",
            }}
            aria-labelledby="hollow"
            name="hollow-model-group"
            value={hollowModel}
            onChange={handleHollowModel}
          >
            <FormControlLabel
              value={YesNoType.Yes}
              control={<Radio />}
              label={t(`stlPrinting.optionYes`)}
            />
            <FormControlLabel
              value={YesNoType.No}
              control={<Radio />}
              label={t(`stlPrinting.optionNo`)}
            />
          </RadioGroup>
        </FormControl>
        <FormControl sx={{ flex: 1 }}>
          <FormLabel>{t("stlPrinting.drainHole")}</FormLabel>
          <RadioGroup
            row
            name="drain-hole-group"
            sx={{
              marginLeft: "8px",
              gap: "40px",
            }}
            value={drainHole}
            onChange={handleDrainHole}
          >
            <FormControlLabel
              value={YesNoType.Yes}
              control={<Radio />}
              label={t(`stlPrinting.optionYes`)}
            />
            <FormControlLabel
              value={YesNoType.No}
              control={<Radio />}
              label={t(`stlPrinting.optionNo`)}
            />
          </RadioGroup>
        </FormControl>
        <FormControl sx={{ flex: 1 }}>
          <FormLabel>{t(`stlPrinting.thicknessModal`)}</FormLabel>
          <TextField
            sx={{
              width: "100px",
              "& .MuiInputBase-input": { padding: "16px 14px" },
            }}
            value={thickness}
            onChange={(e) => {
              handleInput(e, 2, 3, setThickness, debouncedUpdateThickness)
            }}
          />
        </FormControl>
      </Box>
      <Divider sx={{ margin: "16px 0", display: "flex" }} />
      <Box
        id="other2"
        sx={{
          display: "flex",
          ...styleObj,
        }}
      >
        <FormControl sx={{ marginBottom: "12px" }}>
          <FormLabel id="printDirection">
            {t(`stlPrinting.printDirection`)}
          </FormLabel>
          <RadioGroup
            row
            sx={{
              marginLeft: "8px",
              gap: "40px",
            }}
            aria-labelledby="printDirection"
            name="radio-buttons-group"
            value={printDirection}
            onChange={handlePrintDirection}
          >
            <FormControlLabel
              value={PrintDirectionType.Horizontal}
              control={<Radio />}
              label={t(`stlPrinting.Horizontal`)}
            />
            <FormControlLabel
              value={PrintDirectionType.Vertical}
              control={<Radio />}
              label={t(`stlPrinting.Vertical`)}
            />
          </RadioGroup>
        </FormControl>
      </Box>
      <Divider sx={{ margin: "16px 0", display: "flex" }} />
      <Box id="other3" sx={{ display: "flex", ...styleObj }}>
        <FormControl>
          <FormLabel>{t(`stlPrinting.heightOfModel`)}</FormLabel>
          <TextField
            sx={{
              width: "100px",
              "& .MuiInputBase-input": { padding: "16px 14px" },
            }}
            value={heightModel}
            onChange={(e) => {
              handleInput(e, 1, 20, setHeightModel, debouncedUpdateHeight)
            }}
          />
        </FormControl>
      </Box>
    </>
  )
}
